import { Link, type To } from "react-router";
import { paths } from "@shared-core/routing/routes";
import {
  formatCount,
  formatCurrencyGbp,
  formatDateOrDash,
  formatJobTypeLabel,
} from "@shared-core/domain/formatters";
import { isCisOrderNo } from "@shared-core/domain/cis-order-no";
import type { CustomerOrderSummary } from "@shared-core/data/customer-api-types";
import { OrderAgainButton } from "./basket/OrderAgainButton";
import { Badge } from "../../app/components/primitives/badge";
import { cn } from "../../app/components/primitives/utils";
import { ChevronRight } from "lucide-react";

// A row's own link target: the order's page, at the order's first line (`first_order_ref`, set whatever the line
// count and for an orderless job), so a many-item order opens with no redirect hop (doc 48).
//
// A CIS order opens its own page. Its `_self` is that page's read (`/views/customer/order-page/{order_no}`), an API
// address; the portal's own address for it is built here from the number, which only a CIS order has the shape of
// (domain/cis-order-no.ts): nothing else on the row says which system it is from, and nothing should.
export function orderLinkTarget(order: CustomerOrderSummary): string | null {
  if (order.order_no && isCisOrderNo(order.order_no))
    return paths.portalCisOrder({ orderNo: order.order_no });
  if (order._self === null) return null;
  const firstRef = order.first_order_ref ?? order.order_ref;
  return firstRef ? paths.portalOrderDetail({ orderId: firstRef }) : null;
}

export function orderTitle(order: CustomerOrderSummary): string {
  return order.description ?? `Order ${order.order_no}`;
}

// One list, newest first: a customer is never shown, and never asked to know, which system an order lives in.
export function orderRowKey(order: CustomerOrderSummary): string {
  return order.order_no ?? order.order_ref ?? "";
}

// Statuses the badge already says in full: a stage line repeating one of them adds nothing, so the row omits it.
const STATUS_LINES_THE_BADGE_SAYS = new Set([
  "Cancelled",
  "Supplied from stock",
  "Despatched",
  "Delivered",
]);

function StatusBadge({ order }: { order: CustomerOrderSummary }) {
  if (order.awaiting_customer)
    return <Badge variant="warning">Needs you</Badge>;
  if (order.status_line === "Cancelled")
    return <Badge variant="outline">Cancelled</Badge>;
  if (order.status_line === "Supplied from stock")
    return <Badge variant="neutral">Supplied from stock</Badge>;
  // A Tharstern order says "Despatched" (it has left us; arrival is the carrier's to confirm). A CIS order says
  // "Delivered" once its delivery is recorded as arrived.
  if (order.status_line === "Despatched" || order.status_line === "Delivered")
    return <Badge variant="neutral">{order.status_line}</Badge>;
  if (order.is_late) return <Badge variant="error">Late</Badge>;
  return <Badge variant="success">On track</Badge>;
}

// Two shapes of the same grid, chosen by whether any row on the page can offer "Order again": each row is its own
// grid, so a column that existed only on the rows with the button would put the other columns at different places
// from one row to the next. Below a container width of `3xl` the row is stacked (title and status, then date · net · reference, then the
// stage when it says something the badge does not); from there it is one line of columns.
const GRID_WITHOUT_ORDER_AGAIN =
  "grid-cols-[minmax(0,1fr)_auto] @3xl:grid-cols-[5.5rem_minmax(0,3fr)_minmax(7rem,2fr)_5rem_1rem]";
const GRID_WITH_ORDER_AGAIN =
  "grid-cols-[minmax(0,1fr)_auto_2rem] @3xl:grid-cols-[5.5rem_minmax(0,3fr)_minmax(7rem,2fr)_5rem_7.25rem_1rem]";

/**
 * One order in the Orders list, the same row on the Active, All and Despatched tabs.
 *
 * Everything the old card and the old table row showed between them: ordered date, description, what the order is
 * (reference, who placed it, quantity, job, site), where it has got to (badge and stage line) and net. The title is
 * the row's link, stretched over the whole row; "Order again" sits above that link (`z-10`) so the two never nest.
 * Nothing wraps: long text truncates with the full text on `title`, so the row's height does not depend on it.
 */
export function PortalOrderRow({
  order,
  buildPortalUrl,
  showSite,
  hasOrderAgainColumn,
}: {
  order: CustomerOrderSummary;
  buildPortalUrl: (path: string) => To;
  /** Only when the page's rows span more than one site: a single-site customer never needs it repeated. */
  showSite: boolean;
  /** Whether to leave room for "Order again" (see the grid shapes above). */
  hasOrderAgainColumn: boolean;
}) {
  const target = orderLinkTarget(order);
  const title = orderTitle(order);
  const orderedOn = formatDateOrDash(order.ordered_at);
  const net = formatCurrencyGbp(Number(order.net_amount));
  const detail = [
    // Never blank: the customer's own PO or reference, else the number we gave the order (a job's own for an
    // orderless job), so an order placed with no PO still says which order it is.
    order.customer_reference ?? order.order_no ?? order.order_ref ?? undefined,
    // Who placed it, when the server can say: "Placed by you", or "Placed by Sam Price for Priya Shah" for our own
    // staff. The words are the server's, the row just prints them.
    order.placed_by ? `Placed by ${order.placed_by}` : undefined,
    order.line_count > 1
      ? `${formatCount(order.line_count)} items`
      : order.quantity !== null && order.quantity !== undefined
        ? formatCount(order.quantity)
        : undefined,
    formatJobTypeLabel(order.job_no, order.is_call_off) ?? undefined,
    showSite ? (order.site_name ?? undefined) : undefined,
  ].filter(Boolean) as string[];
  const stage = STATUS_LINES_THE_BADGE_SAYS.has(order.status_line)
    ? null
    : order.status_line;
  const stageTone = order.awaiting_customer
    ? "font-medium text-amber-700 dark:text-amber-400"
    : order.is_late
      ? "font-medium text-red-700 dark:text-red-400"
      : "text-muted-foreground";

  return (
    <li
      data-testid="order-row"
      className={cn(
        "relative grid items-center gap-x-3 border-l-[3px] border-l-transparent px-3 py-2.5",
        hasOrderAgainColumn ? GRID_WITH_ORDER_AGAIN : GRID_WITHOUT_ORDER_AGAIN,
        target !== null && "transition-colors hover:bg-accent/40",
        order.awaiting_customer && "border-l-amber-400",
      )}
    >
      <span className="col-start-1 row-start-1 hidden whitespace-nowrap text-sm text-muted-foreground @3xl:row-span-2 @3xl:block">
        {orderedOn}
      </span>

      <div className="col-start-1 row-start-1 min-w-0 @3xl:col-start-2">
        {target !== null ? (
          <Link
            to={buildPortalUrl(target)}
            title={title}
            className="block truncate text-sm font-medium after:absolute after:inset-0 after:rounded-sm hover:underline focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring/50"
          >
            {title}
          </Link>
        ) : (
          <span title={title} className="block truncate text-sm font-medium">
            {title}
          </span>
        )}
      </div>

      <p
        title={detail.join(" · ")}
        className="col-span-2 col-start-1 row-start-2 min-h-4 min-w-0 truncate text-xs text-muted-foreground @3xl:col-span-1 @3xl:col-start-2"
      >
        <span className="@3xl:hidden">
          {orderedOn} · {net}
          {detail.length > 0 ? " · " : ""}
        </span>
        {detail.join(" · ")}
      </p>

      <div
        className={cn(
          "col-start-2 row-start-1 @3xl:col-start-3",
          stage === null && "@3xl:row-span-2",
        )}
      >
        <StatusBadge order={order} />
      </div>
      {stage !== null && (
        <p
          title={stage}
          className={cn(
            "col-span-2 col-start-1 row-start-3 mt-0.5 min-w-0 truncate text-xs @3xl:col-span-1 @3xl:col-start-3 @3xl:row-start-2 @3xl:mt-0",
            stageTone,
          )}
        >
          {stage}
        </p>
      )}

      <span className="hidden whitespace-nowrap text-right text-sm tabular-nums @3xl:col-start-4 @3xl:row-span-2 @3xl:row-start-1 @3xl:block">
        {net}
      </span>

      {hasOrderAgainColumn && (
        <div className="relative z-10 col-start-3 row-span-3 row-start-1 justify-self-end @3xl:col-start-5 @3xl:row-span-2">
          <OrderAgainButton
            compact
            reorderUrl={order.reorder_url}
            orderLabel={title}
          />
        </div>
      )}

      <ChevronRight
        aria-hidden="true"
        className={cn(
          "hidden size-4 text-muted-foreground @3xl:row-span-2 @3xl:row-start-1 @3xl:block",
          hasOrderAgainColumn ? "@3xl:col-start-6" : "@3xl:col-start-5",
          target === null && "invisible",
        )}
      />
    </li>
  );
}
